import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Múi giờ chuẩn của app: GMT+7 (Việt Nam). */
export const VN_TZ = "Asia/Ho_Chi_Minh";

/** Trả về ngày hôm nay theo VN ở dạng "YYYY-MM-DD" — không phụ thuộc tz máy. */
export function todayVN(): string {
  // en-CA cho ISO "YYYY-MM-DD"
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: VN_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

/** Trả về một Date đại diện "thời điểm hiện tại ở VN" — chỉ dùng để đọc year/month/day/hour. */
export function nowVNDate(): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: VN_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  // Build a local Date với các giá trị giờ VN — KHÔNG dùng để serialize lại sang UTC.
  return new Date(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
}

/** Cộng/trừ ngày cho chuỗi "YYYY-MM-DD" mà không đi qua timezone. */
export function addDaysISO(iso: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  // Dùng Date.UTC để cộng ngày an toàn rồi format lại chuẩn YYYY-MM-DD.
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + n * 86400_000;
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** Format ISO date string ("YYYY-MM-DD" or full ISO) or Date to "dd/MM/yyyy". */
export function fmtDate(input: string | Date | null | undefined): string {
  if (!input) return "";
  if (typeof input === "string") {
    // Pure date "YYYY-MM-DD"
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(input);
    if (isNaN(d.getTime())) return input;
    return d.toLocaleDateString("vi-VN", { timeZone: VN_TZ });
  }
  return input.toLocaleDateString("vi-VN", { timeZone: VN_TZ });
}

/** Format ISO datetime to "dd/MM/yyyy HH:mm". */
export function fmtDateTime(input: string | Date | null | undefined): string {
  if (!input) return "";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return String(input);
  return d.toLocaleString("vi-VN", {
    timeZone: VN_TZ,
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ───────────────────────────────────────────────────────────
// Helpers chuẩn hóa ngày giờ Việt Nam (GMT+7).
// UI luôn hiển thị DD/MM/YYYY; backend/DB vẫn lưu ISO YYYY-MM-DD.
// ───────────────────────────────────────────────────────────

/** Alias hiển thị DD/MM/YYYY — dùng tên rõ ràng ở UI mới. */
export const formatDateVN = fmtDate;

/** Alias hiển thị DD/MM/YYYY HH:mm theo GMT+7. */
export const formatDateTimeVN = fmtDateTime;

/** Hôm nay theo GMT+7 dạng "YYYY-MM-DD" — alias của todayVN. */
export const todayISOInVN = todayVN;

/** Hiển thị "MM/YYYY" từ "YYYY-MM", "YYYY-MM-DD" hoặc Date. */
export function formatMonthVN(input: string | Date | null | undefined): string {
  if (!input) return "";
  if (typeof input === "string") {
    const m = input.match(/^(\d{4})-(\d{2})/);
    if (m) return `${m[2]}/${m[1]}`;
  }
  const d = typeof input === "string" ? new Date(input) : input;
  if (!d || isNaN(d.getTime())) return String(input ?? "");
  return d.toLocaleDateString("vi-VN", { timeZone: VN_TZ, month: "2-digit", year: "numeric" });
}

/**
 * Parse "YYYY-MM-DD" thành Date theo LOCAL (không qua UTC) để tránh lệch ngày
 * khi máy ở múi giờ khác GMT+7. KHÔNG dùng new Date("YYYY-MM-DD") trực tiếp.
 */
export function parseISODateLocal(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Parse chuỗi ngày người dùng nhập theo VN ("DD/MM/YYYY", "D/M/YY", có thể dùng
 * dấu '-' hoặc '.') về ISO "YYYY-MM-DD". Cũng chấp nhận sẵn "YYYY-MM-DD" để
 * tương thích dữ liệu cũ / CSV ISO. Trả "" nếu không nhận diện được.
 */
export function parseVNDateToISO(input: string | null | undefined): string {
  if (input == null) return "";
  const s = String(input).trim();
  if (s === "") return "";
  // Đã là ISO YYYY-MM-DD?
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // DD/MM/YYYY hoặc DD-MM-YYYY hoặc DD.MM.YYYY (cho 2 hoặc 4 chữ số năm).
  const m = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/.exec(s);
  if (!m) return "";
  const d = Number(m[1]);
  const mo = Number(m[2]);
  let y = Number(m[3]);
  if (y < 100) y += y >= 70 ? 1900 : 2000;
  if (!d || !mo || d > 31 || mo > 12) return "";
  return `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
